import { TUNABLES } from "../config";
import {
  type AxialCoord,
  axialToPixel,
  type HexState,
  hexCorners,
  type PixelPoint,
  type RunState,
} from "../core";
import {
  COURT_COLOR,
  ENEMY_FILL,
  LABEL_COLOR,
  PLAYER_FILL,
  scaleColor,
  UNCLAIMED_FILL,
} from "../render/color";

/**
 * The day's map on the front page (B3.1): the King's run of the day at tick 0, drawn with the
 * canvas's own 2D context so it shows before Pixi is fetched. The palette is the game's, so the
 * picture is the map the visitor is about to play. `drawMapInto` paints it into a canvas of the
 * page, the landing's and each duel card's (B3.2).
 */

/** Half a pointy-top hex's width at size 1; its height reaches 1 above and below the centre. */
const HALF_WIDTH_AT_ONE = Math.sqrt(3) / 2;

export interface HeroLayout {
  /** Centre-to-corner radius of a hex, in CSS px. */
  readonly size: number;
  /** Where the hex at q = 0, r = 0 has its centre, in CSS px. */
  readonly originX: number;
  readonly originY: number;
}

/** The largest hex size that keeps every hex inside the box, `padding` clear of its edges, centred. */
export function heroLayout(
  coords: readonly AxialCoord[],
  width: number,
  height: number,
  padding: number,
): HeroLayout {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const coord of coords) {
    const centre = axialToPixel(coord, 1);
    minX = Math.min(minX, centre.x - HALF_WIDTH_AT_ONE);
    maxX = Math.max(maxX, centre.x + HALF_WIDTH_AT_ONE);
    minY = Math.min(minY, centre.y - 1);
    maxY = Math.max(maxY, centre.y + 1);
  }
  const size = Math.max(
    0,
    Math.min((width - 2 * padding) / (maxX - minX), (height - 2 * padding) / (maxY - minY)),
  );
  return {
    size,
    originX: width / 2 - (size * (minX + maxX)) / 2,
    originY: height / 2 - (size * (minY + maxY)) / 2,
  };
}

/** A hex's colour: its owner's, darker on rough ground. */
export function heroFill(hex: Pick<HexState, "owner" | "terrain">): number {
  const base =
    hex.owner === "player" ? PLAYER_FILL : hex.owner === "enemy" ? ENEMY_FILL : UNCLAIMED_FILL;
  return hex.terrain === "rough" ? scaleColor(base, TUNABLES.landing.roughShade) : base;
}

function css(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

/** A keep with three merlons: a city, the prize of the first minute. */
function drawKeep(context: CanvasRenderingContext2D, centre: PixelPoint, size: number): void {
  const width = size * 0.62;
  const height = size * 0.5;
  const merlon = width / 5;
  const left = centre.x - width / 2;
  const top = centre.y - height / 2;
  context.fillStyle = css(LABEL_COLOR);
  context.fillRect(left, top + merlon, width, height - merlon);
  for (const step of [0, 2, 4]) {
    context.fillRect(left + step * merlon, top, merlon, merlon);
  }
}

/** A well: the court's gold ring. */
function drawWell(context: CanvasRenderingContext2D, centre: PixelPoint, size: number): void {
  context.strokeStyle = css(COURT_COLOR);
  context.lineWidth = Math.max(1, size * 0.1);
  context.beginPath();
  context.arc(centre.x, centre.y, size * 0.24, 0, Math.PI * 2);
  context.stroke();
}

/** How a map is fitted: the box in CSS px, the space kept clear of its edges, the gap between tiles. */
export interface MapBox {
  readonly width: number;
  readonly height: number;
  readonly padding: number;
  readonly seam: number;
}

/** Draws the run's land into a box of the context: the day's map, or a duel card's (B3.2). */
export function drawHeroMap(context: CanvasRenderingContext2D, state: RunState, box: MapBox): void {
  const { width, height } = box;
  const layout = heroLayout(
    state.hexes.map((hex) => hex.coord),
    width,
    height,
    box.padding,
  );
  context.clearRect(0, 0, width, height);
  const tileSize = Math.max(0, layout.size - box.seam / 2);
  for (const hex of state.hexes) {
    const offset = axialToPixel(hex.coord, layout.size);
    const centre = { x: layout.originX + offset.x, y: layout.originY + offset.y };
    const corners = hexCorners(centre, tileSize);
    context.beginPath();
    corners.forEach((corner, index) => {
      if (index === 0) {
        context.moveTo(corner.x, corner.y);
      } else {
        context.lineTo(corner.x, corner.y);
      }
    });
    context.closePath();
    context.fillStyle = css(heroFill(hex));
    context.fill();
    if (hex.structure === "city") {
      drawKeep(context, centre, layout.size);
    } else if (hex.structure === "well") {
      drawWell(context, centre, layout.size);
    }
  }
}

/** A sharper canvas than this costs memory and paints nothing the eye can tell. */
const MAX_PIXEL_RATIO = 2;

/** Draws a run's land into a canvas at its CSS size and the screen's pixel ratio. */
export function drawMapInto(
  canvas: HTMLCanvasElement,
  run: RunState,
  padding: number,
  seam: number,
): void {
  const box = canvas.getBoundingClientRect();
  const ratio = Math.min(globalThis.devicePixelRatio || 1, MAX_PIXEL_RATIO);
  canvas.width = Math.round(box.width * ratio);
  canvas.height = Math.round(box.height * ratio);
  const context = canvas.getContext("2d");
  if (context && box.width > 0 && box.height > 0) {
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    drawHeroMap(context, run, { width: box.width, height: box.height, padding, seam });
  }
  canvas.dataset.hexes = String(run.hexes.length);
}
